import { Dispatch, memo, SetStateAction, useMemo, useState } from "react";
import { VisibilityState } from "@tanstack/table-core";
import { ContentDivider } from "../../../../../ContentDivider";
import { TextField } from "../../../../../inputs";
import { useTableContext } from "../../../../context";
import { renderColumnHeaderAsText } from "../../../../utils";
import { ColumnsBlock } from "./ColumnsBlock";

import { ReactComponent as SearchIcon } from "./icons/search.svg";

export type ColumnsSettingsProps = {
  columnVisibility: VisibilityState,
  setColumnVisibility: Dispatch<SetStateAction<VisibilityState>>,
  columnOrder: string[],
  setColumnOrder: Dispatch<SetStateAction<string[]>>,
}

export const ColumnsSettings = memo<ColumnsSettingsProps>(({ columnVisibility, setColumnVisibility, setColumnOrder, columnOrder }) => {
  const table = useTableContext();
  const [ columnsFilter, setColumnsFilter ] = useState<string | null>("");

  const allLeafColumns = table!.getAllLeafColumns();
  const allColumns = useMemo(() => {
    return allLeafColumns
      .filter(c => !c.columnDef.meta?.hideInSettings)
      .map(c => ({
        ...c,
        headerTitleForSearch: renderColumnHeaderAsText(c),
      }));
  }, [ allLeafColumns ]);


  const sortedColumns = useMemo(() => {
    return allColumns.sort((a, b) => {
      let aIndex = columnOrder.indexOf(a.id);
      if (aIndex === -1) {
        aIndex = allColumns.indexOf(a) + 1;
      }
      let bIndex = columnOrder.indexOf(b.id);
      if (bIndex === -1) {
        bIndex = allColumns.indexOf(b) + 1;
      }
      return aIndex - bIndex;
    });
  }, [ allColumns, columnOrder ]);


  const hasColumnFilter = columnsFilter !== null && columnsFilter !== "";
  const filteredColumns = sortedColumns
    .filter(column => hasColumnFilter
      ? column.headerTitleForSearch.toLowerCase()
        .includes(columnsFilter.toLowerCase())
      : true,
    );
  const activeColumns = filteredColumns
    .filter(column => columnVisibility[column.id] ?? true);
  const otherColumns = filteredColumns
    .filter(column => !(columnVisibility[column.id] ?? true));

  return (
    <>
      <TextField
        startAdornment={<SearchIcon />}
        label="Columns"
        placeholder="Search column options"
        value={columnsFilter}
        onChange={setColumnsFilter}
        isClearable
      />
      {
        !!activeColumns?.length &&
        <ColumnsBlock
          label="Active columns"
          columns={filteredColumns}
          filterValues={column => (columnVisibility[column.id] ?? true) && column.columnDef.meta!.availableInSettings!}
          dndDisabled={activeColumns.length < 2 || hasColumnFilter}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
          onChange={columns => setColumnOrder(columns.map(x => x.id))}
        />
      }
      {
        !!otherColumns?.length &&
        <>
          <ContentDivider contentDividerStyle="singleLine" />
          <ColumnsBlock
            label="Other columns"
            columns={filteredColumns}
            filterValues={column => !(columnVisibility[column.id] ?? true) && column.columnDef.meta!.availableInSettings!}
            dndDisabled
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
          />
        </>
      }
    </>
  );
});